import React, { useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import FormInput from "@/components/FormInput";
import { SectionTitle, SectionDivider } from "./SizingToolFormLayout";
import AccountNumberInput from "@/components/FormInput/AccountNumberInput";

interface CustomerInformationSectionProps {
  formInstance: any;
  sizingForm: any;
  setSizingForm: (form: any) => void;
  disabled?: boolean;
  skipDuplicateCheck?: boolean;
  isSizingTool?: boolean;
  skipValidation?: boolean;
}

export const CustomerInformationSection: React.FC<
  CustomerInformationSectionProps
> = ({
  formInstance,
  setSizingForm,
  disabled = false,
  skipDuplicateCheck = false,
  isSizingTool = false,
  skipValidation = false,
}) => {
  const accountNumber = formInstance.watch("accountNumber");
  const lastAccountNumberRef = useRef<string | null>(null);

  useEffect(() => {
    if (accountNumber && accountNumber !== lastAccountNumberRef.current) {
      lastAccountNumberRef.current = accountNumber;
      setSizingForm((prev: any) => ({
        ...prev,
        accountNumber,
      }));
    }
  }, [accountNumber, setSizingForm]);

  const handleCustomerDataPopulated = useCallback(
    (customerData: any) => {
      const updates = {
        accountNumber:
          customerData.accountNumber ||
          customerData.accountHash ||
          accountNumber,
        customerName: customerData.customerName,
      };

      setSizingForm((prev: any) => ({
        ...prev,
        ...updates,
      }));
    },
    [setSizingForm, accountNumber]
  );

  return (
    <>
      <SectionTitle>Customer Information</SectionTitle>
      <SectionDivider />

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <AccountNumberInput
            name="accountNumber"
            label="Account Number"
            placeholder="123456789012"
            disabled={disabled}
            onCustomerDataPopulated={handleCustomerDataPopulated}
            hideCustomerDataDisplay={true}
            zipCodeName="zipCode"
            skipDuplicateCheck={skipDuplicateCheck}
            isSizingTool={isSizingTool}
            skipValidation={skipValidation}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormInput
            name="customerName"
            control={formInstance.control}
            label="Account Name"
            placeholder="Add customer name"
            disabled={disabled}
            readOnly={true}
            controlProps={{
              rules: {
                required: "Required",
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormInput
          name="zipCode"
          control={formInstance.control}
          label="Zip Code"
          placeholder="92101"
          disabled={disabled}
          controlProps={{
            rules: {
              required: "Zip code is required",
              pattern: {
                value: /^\d{5}$/,
                message: "Zip code must be exactly 5 digits",
              },
            },
          }}
          inputProps={{
            maxLength: 5,
            onKeyPress: (e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            },
          }}
        />
      </Box>
    </>
  );
};

export default CustomerInformationSection;
